/**
 * Application-wide constants
 * Centralized configuration values to avoid magic numbers and strings
 */

// HTTP Status Codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
};

// Time Constants (in milliseconds)
export const TIME_CONSTANTS = {
  SECOND: 1000,
  MINUTE: 60 * 1000,
  HOUR: 60 * 60 * 1000,
  DAY: 24 * 60 * 60 * 1000,
  WEEK: 7 * 24 * 60 * 60 * 1000,
  MONTH: 30 * 24 * 60 * 60 * 1000,
  YEAR: 365 * 24 * 60 * 60 * 1000,
};

// API Timeout Constants
export const API_TIMEOUT = {
  DEFAULT: 30000, // 30 seconds
  SHORT: 10000, // 10 seconds
  LONG: 60000, // 60 seconds
};

// Debounce Delays (in milliseconds)
export const DEBOUNCE_DELAY = {
  SEARCH: 300,
  INPUT: 500,
  API_CALL: 1000,
};

// File Upload Constants
export const FILE_CONSTANTS = {
  MAX_IMAGE_SIZE: 2 * 1024 * 1024, // 2MB in bytes
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB in bytes
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  ALLOWED_FILE_TYPES: ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'],
  MAX_FILENAME_LENGTH: 255,
};

// Validation Constants
export const VALIDATION_CONSTANTS = {
  PASSWORD_MIN_LENGTH: 6,
  PASSWORD_MAX_LENGTH: 12,
  NAME_MIN_LENGTH: 2,
  NAME_MAX_LENGTH: 100,
  EMAIL_MAX_LENGTH: 255,
  PHONE_LENGTH: 10,
  MESSAGE_MIN_LENGTH: 10,
  MESSAGE_MAX_LENGTH: 2000,
  BIO_MAX_LENGTH: 1000,
  URL_MAX_LENGTH: 500,
  AGE_MIN: 10,
  AGE_MAX: 120,
  HEIGHT_MIN: 100,
  HEIGHT_MAX: 250,
  WEIGHT_MIN: 20,
  WEIGHT_MAX: 200,
  MAX_FILENAME_LENGTH: 255,
};

// Status Constants
export const STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  PENDING_APPROVAL: 'pending_approval',
};

// Payment Status Constants
export const PAYMENT_STATUS = {
  PENDING: 'pending',
  PAID: 'paid',
  OVERDUE: 'overdue',
  REFUNDED: 'refunded',
  PARTIAL: 'partial',
};

// User Roles
export const USER_ROLES = {
  USER: 'user',
  TRAINER: 'trainer',
  ADMIN: 'admin',
  SUPER_ADMIN: 'super_admin',
};

// Gender Options
export const GENDER_OPTIONS = {
  MALE: 'male',
  FEMALE: 'female',
  OTHER: 'other',
};

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK: 'Network error. Please check your connection and try again.',
  UNAUTHORIZED: 'Please log in to continue.',
  FORBIDDEN: 'You don\'t have permission to do that.',
  NOT_FOUND: 'Resource not found.',
  CONFLICT: 'Conflict. This action cannot be completed.',
  VALIDATION: 'Validation error. Please check your input.',
  TOO_MANY_REQUESTS: 'Too many requests. Please try again later.',
  SERVER_ERROR: 'We’re having trouble right now. Please try again in a moment.',
  SERVICE_UNAVAILABLE: 'Service unavailable. Please try again later.',
  UNKNOWN: 'Something went wrong. Please try again.',
  NO_RESPONSE: 'No response from server. Please try again.',
  SESSION_EXPIRED: 'Session expired. Please log in again.',
  INVALID_TOKEN: 'Invalid or expired token. Please request a new one.',
  FAILED_TO_LOAD: 'Failed to load data. Please try again.',
  FAILED_TO_SAVE: 'Failed to save changes. Please try again.',
  FAILED_TO_DELETE: 'Failed to delete. Please try again.',
};

// Success Messages
export const SUCCESS_MESSAGES = {
  SAVED: 'Changes saved successfully',
  DELETED: 'Deleted successfully',
  CREATED: 'Created successfully',
  UPDATED: 'Updated successfully',
  LOGIN: 'Login successful!',
  LOGOUT: 'Logout successful!',
  PASSWORD_RESET: 'Password reset successfully!',
  EMAIL_SENT: 'Email sent successfully',
  PROFILE_UPDATED: 'Profile updated successfully',
};

// Date Format Constants
export const DATE_FORMATS = {
  DD_MM_YYYY: 'DD/MM/YYYY',
  MM_DD_YYYY: 'MM/DD/YYYY',
  YYYY_MM_DD: 'YYYY-MM-DD',
};

// Time Format Constants
export const TIME_FORMATS = {
  TWELVE_HOUR: '12-hour',
  TWENTY_FOUR_HOUR: '24-hour',
};

// Chart Colors
export const CHART_COLORS = {
  PRIMARY: '#0088FE',
  SUCCESS: '#00C49F',
  WARNING: '#FFBB28',
  DANGER: '#FF8042',
  INFO: '#8884D8',
  COLORS_ARRAY: ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'],
};

// Pagination Constants
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 12,
  DEFAULT_PAGE_SIZE: 10,
  MAX_PAGE_SIZE: 100,
};

// Local Storage Keys
export const STORAGE_KEYS = {
  THEME: 'theme',
  LANGUAGE: 'language',
  PREFERENCES: 'preferences',
};

// API Endpoint Prefixes
export const API_PREFIXES = {
  AUTH: '/auth',
  STUDENTS: '/api/students',
  TRAINERS: '/api/trainers',
  PROGRAMS: '/api/programs',
  CLASSES: '/api/classes',
  FEE_PLAN: '/api/fee-plan',
  ADMIN: '/api/admin',
  TRANSACTIONS: '/api/transactions',
};

// Regex Patterns
export const REGEX_PATTERNS = {
  EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  PHONE: /^(\+91)?[6-9]\d{9}$|^[0-9]{10}$/,
  PHONE_INDIA: /^[6-9]\d{9}$/,
  NAME: /^[a-zA-Z\s'-]+$/,
  URL: /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
  PASSWORD: /^(?=.*[a-zA-Z])(?=.*[0-9])/,
  ALPHANUMERIC: /^[a-zA-Z0-9]+$/,
  OBJECT_ID: /^[a-zA-Z0-9\-_]{20,500}$/,
};

// Default Values
export const DEFAULT_VALUES = {
  PROFILE_IMAGE: '/images/default-profile.jpg',
  AVATAR_MALE: '/images/default-profile.jpg',
  AVATAR_FEMALE: '/images/avatar-fem.jpg',
  COUNTRY_CODE: '+91',
  DATE_RANGE_DAYS: 365, // Default date range in days for transaction filters
  DEBOUNCE_SEARCH: 300, // Default debounce delay for search inputs (ms)
};

// Toast Configuration
export const TOAST_CONFIG = {
  AUTO_CLOSE: 3000,
  AUTO_CLOSE_SHORT: 1000,
  AUTO_CLOSE_LONG: 5000,
  POSITION: 'top-center',
};

// Loading States
export const LOADING_STATES = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};

// PWA Constants
export const PWA_CONFIG = {
  SERVICE_WORKER_VERSION: '1.0.0',
  CACHE_NAMES: {
    IMAGES: 'images-cache',
    FONTS: 'fonts-cache',
    API: 'api-cache',
    API_DATA: 'api-data-cache',
    HTML: 'html-cache',
    STATIC: 'static-resources-cache',
  },
  NOTIFICATION: {
    DEFAULT_ICON: '/icons/icon-192x192.png',
    BADGE: '/icons/icon-96x96.png',
    DEFAULT_TITLE: 'Yogvital',
    DEFAULT_TAG: 'yogvital-notification',
  },
  BACKGROUND_SYNC: {
    TAG_PREFIX: 'yogvital-sync-',
    MAX_RETRY_ATTEMPTS: 3,
    RETRY_DELAY: 5000, // 5 seconds
  },
  INSTALL_PROMPT: {
    STORAGE_KEY: 'pwa-install-dismissed',
    DISMISS_DURATION: 7 * 24 * 60 * 60 * 1000, // 7 days
  },
  UPDATE_CHECK: {
    INTERVAL: 60 * 60 * 1000, // 1 hour
    STORAGE_KEY: 'pwa-update-available',
  },
};

// Push Notification Constants
export const PUSH_NOTIFICATION = {
  VAPID_PUBLIC_KEY: import.meta.env.VITE_VAPID_PUBLIC_KEY || '',
  SUBSCRIPTION_ENDPOINT: '/api/push/subscribe',
  PERMISSION: {
    GRANTED: 'granted',
    DENIED: 'denied',
    DEFAULT: 'default',
  },
};

